import type {
  TooltipDeleteOverrideBody,
  TooltipEffectiveResponse,
  TooltipListResponse,
  TooltipPutBody,
  TooltipSeedBody,
  TooltipSeedResponse,
} from "./types";

/** Dev server uses setupProxy with relative URLs; deployed builds call the API host directly. */
const ENDPOINT_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_SERVICE_URL || "").replace(/\/$/, "")
    : "";

const tooltipJsonHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

export const getTooltips = async (
  productCode: string,
): Promise<Response> => {
  const q = encodeURIComponent(productCode);
  return fetch(`${ENDPOINT_URL}/OnBoarding/tooltips?productCode=${q}`, {
    method: "GET",
    credentials: "include",
    headers: tooltipJsonHeaders,
  });
};

export const getEffectiveTooltips = async (
  productCode: string,
): Promise<Response> => {
  const q = encodeURIComponent(productCode);
  return fetch(
    `${ENDPOINT_URL}/OnBoarding/tooltips/effective?productCode=${q}`,
    {
      method: "GET",
      credentials: "include",
      headers: tooltipJsonHeaders,
    },
  );
};

export const putTooltips = async (body: TooltipPutBody): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/tooltips`, {
    method: "PUT",
    credentials: "include",
    headers: tooltipJsonHeaders,
    body: JSON.stringify(body),
  });
};

/** Backend embeds the catalog; body is `{ productCode }` or `{}` to seed all. */
export const seedTooltips = async (
  body: TooltipSeedBody = {},
): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/tooltips/seed`, {
    method: "POST",
    credentials: "include",
    headers: tooltipJsonHeaders,
    body: JSON.stringify(body ?? {}),
  });
};

export const deleteTooltipOverride = async (
  body: TooltipDeleteOverrideBody,
): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/tooltips/override`, {
    method: "DELETE",
    credentials: "include",
    headers: tooltipJsonHeaders,
    body: JSON.stringify(body),
  });
};

export async function parseTooltipSeedResponse(
  response: Response,
): Promise<TooltipSeedResponse | null> {
  try {
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    const record = data as Record<string, unknown>;
    const productCodes = Array.isArray(record.productCodes)
      ? record.productCodes.map((c) => String(c))
      : undefined;
    return {
      inserted:
        typeof record.inserted === "number" ? record.inserted : undefined,
      alreadyPresent:
        typeof record.alreadyPresent === "number"
          ? record.alreadyPresent
          : undefined,
      productCodes,
      message:
        typeof record.message === "string" ? record.message : undefined,
    };
  } catch {
    return null;
  }
}

export async function parseTooltipListResponse(
  response: Response,
): Promise<TooltipListResponse | null> {
  if (!response.ok) return null;
  try {
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      productCode: String(data.productCode ?? ""),
      items: items.map((raw: Record<string, unknown>) => ({
        productCode: String(raw.productCode ?? ""),
        groupKey: String(raw.groupKey ?? ""),
        fieldKey: String(raw.fieldKey ?? ""),
        defaultText: String(raw.defaultText ?? ""),
        text: String(raw.text ?? raw.defaultText ?? ""),
        hasOverride: Boolean(raw.hasOverride),
        updatedAt: raw.updatedAt == null ? null : String(raw.updatedAt),
        updatedByUsername:
          raw.updatedByUsername == null
            ? null
            : String(raw.updatedByUsername),
      })),
    };
  } catch {
    return null;
  }
}

export async function parseTooltipEffectiveResponse(
  response: Response,
): Promise<TooltipEffectiveResponse | null> {
  if (!response.ok) return null;
  try {
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      productCode: String(data.productCode ?? ""),
      items: items.map((raw: Record<string, unknown>) => ({
        groupKey: String(raw.groupKey ?? ""),
        fieldKey: String(raw.fieldKey ?? ""),
        text: String(raw.text ?? ""),
      })),
    };
  } catch {
    return null;
  }
}
